import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { FaAngleLeft, FaAngleRight } from "react-icons/fa6";
import { useTranslation } from "react-i18next";

export const Temoignages = () => {
  const { i18n, t } = useTranslation();
  const baseUrl = useSelector((state) => state.gtm.baseUrl);
  const [isLoading, setIsLoading] = useState(true);
  const [types, setTypes] = useState([]);
  const navigationRefs = useRef({});
  // const lang = i18n.language;
  const language = localStorage.getItem("i18nextLng");

  useEffect(() => {
    let active = true;
    const fetchTestimonials = async () => {
      setIsLoading(true);
      try {
        const { data } = await axios.get(`${baseUrl}/testimonials`);
        if (!active) return;

        const sortedTypes = data.types.sort((a, b) => a.order - b.order);
        setTypes(sortedTypes);
      } catch (err) {
        console.error(err);
      } finally {
        if (active) setIsLoading(false);
      }
    };
    fetchTestimonials();
    return () => {
      active = false;
    };
  }, [baseUrl]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [video, setVideo] = useState("");

  return (
    <div className="min-h-screen pt-8 ">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div
          id="Temoignages"
          className="bg-white rounded-xl shadow-md p-6 sm:p-8"
        >
          <h1 className="text-2xl sm:text-3xl lg:text-4xl text-center mb-8 relative after:content-[''] after:absolute after:w-14 after:h-px after:bottom-[-13px] after:left-1/2 after:transform after:-translate-x-1/2 after:bg-black karla-font leading-relaxed tracking-wide">
            {t("aboutUs_content.testimonials")}
          </h1>

          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="w-9 h-9 border-4 border-black border-l-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="space-y-12">
              {types?.map((item) => {
                if (item.testimonials.length > 0) {
                  if (!navigationRefs.current[item.id]) {
                    navigationRefs.current[item.id] = {
                      prevRef: React.createRef(),
                      nextRef: React.createRef(),
                    };
                  }
                  const { prevRef, nextRef } = navigationRefs.current[item.id];
                  return (
                    <div key={item.id} className="space-y-8">
                      <h2 className="text-2xl md:text-3xl font-bold text-center bg-gray-100 py-5 px-6 rounded-xl border-l-4 border-[#119f04]">
                        {language === "fr" ? `${item.fr_name}` : `${item.name}`}
                      </h2>

                      <div className="relative px-1">
                        <button
                          ref={prevRef}
                          className="absolute top-1/2 left-[-30px] transform -translate-y-1/2 z-10 text-2xl rounded-full text-black p-4 bg-white shadow-md hover:bg-[#119f04] hover:text-white transition-all"
                        >
                          <FaAngleLeft />
                        </button>

                        <button
                          ref={nextRef}
                          className="absolute top-1/2 right-[-30px] transform -translate-y-1/2 z-10 text-2xl rounded-full text-black p-4 bg-white shadow-md hover:bg-[#119f04] hover:text-white transition-all"
                        >
                          <FaAngleRight />
                        </button>

                        <Swiper
                          spaceBetween={25}
                          loop={true}
                          breakpoints={{
                            0: { slidesPerView: 1 },
                            768: { slidesPerView: 2 },
                            1024: { slidesPerView: 3 },
                          }}
                          navigation={{
                            prevEl: prevRef.current,
                            nextEl: nextRef.current,
                          }}
                          modules={[Navigation, Pagination]}
                          autoplay={{
                            delay: 3000,
                            disableOnInteraction: false,
                          }}
                          onSwiper={(swiper) => {
                            setTimeout(() => {
                              const { prevRef, nextRef } =
                                navigationRefs.current[item.id];

                              if (
                                swiper.params &&
                                swiper.params.navigation &&
                                prevRef.current &&
                                nextRef.current
                              ) {
                                swiper.params.navigation.prevEl =
                                  prevRef.current;
                                swiper.params.navigation.nextEl =
                                  nextRef.current;

                                if (
                                  swiper.navigation &&
                                  typeof swiper.navigation.init === "function"
                                ) {
                                  swiper.navigation.init();
                                  swiper.navigation.update();
                                }
                              }
                            }, 0);
                          }}
                          className="w-full"
                        >
                          {item.testimonials?.map((testimonial, idx) => (
                            <SwiperSlide key={idx}>
                              <div className="border bg-[#ebebeb] border-black p-4 relative group h-[500px] overflow-hidden">
                                <div className="video-container relative h-96 w-full">
                                  <video
                                    className="video-background absolute inset-0 w-full h-full object-cover"
                                    loop
                                    muted
                                  >
                                    <source
                                      src={testimonial.url}
                                      type="video/mp4"
                                    />
                                  </video>

                                  <div className="video-overlay absolute inset-0 bg-black bg-opacity-60"></div>

                                  <div className="absolute inset-0 flex items-center justify-center">
                                    <div
                                      className="w-16 h-16 cursor-pointer transition-transform duration-300 group-hover:scale-110"
                                      onClick={() => {
                                        setVideo(testimonial.url);
                                        setIsModalOpen(true);
                                      }}
                                    >
                                      <svg
                                        viewBox="0 0 24 24"
                                        fill="white"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <path d="M8 5v14l11-7z" />
                                      </svg>
                                    </div>
                                  </div>
                                </div>

                                <h3 className="mt-4 text-center text-lg font-medium">
                                  {testimonial.name}
                                </h3>
                              </div>
                            </SwiperSlide>
                          ))}
                        </Swiper>
                      </div>
                    </div>
                  );
                }
                return null;
              })}
            </div>
          )}
        </div>
      </div>

      {isModalOpen && (
        <div className="modal fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50">
          <div className="modal-video relative lg:max-w-[80vw] lg:h-[80vh] bg-white">
            <button
              className="modal-close absolute -top-12 right-0 text-white text-3xl"
              onClick={() => setIsModalOpen(false)}
            >
              ×
            </button>
            <video className="w-full h-full" controls autoPlay src={video}>
              <source src={video} type="video/mp4" />
            </video>
          </div>
        </div>
      )}
    </div>
  );
};
